import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Chip
} from '@mui/material';
import { useData } from '../../context/DataContext';
import BusinessIcon from '@mui/icons-material/Business';
import hospitalImage from '../../assets/homepageimages/hospital-image.webp';
import PageHero from './PageHero';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const Board = () => {
  const { state } = useData();
  const { pages, boardMembers } = state;
  const boardData = pages.board;

  return (
    <Box>
      <PageHero
        src={hospitalImage}
        alt="Board of Directors"
        title={boardData?.title || 'Board of Directors'}
        subtitle="Strategic leadership and governance"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Description */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h5" paragraph sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
            {boardData?.description || 'Our Board of Directors provides strategic leadership and governance to ensure the hospital continues to meet the highest standards of healthcare excellence.'}
          </Typography>
        </Box>

        {/* Board Members */}
        <Grid container spacing={4}>
          {boardMembers && boardMembers.length > 0 ? (
            boardMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={member.id || index}>
                <Card sx={{ 
                  height: '100%', 
                  p: 3,
                  textAlign: 'center',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}>
                  <CardContent>
                    <Avatar
                      src={member.image}
                      alt={member.name}
                      sx={{ 
                        width: 120, 
                        height: 120, 
                        mx: 'auto', 
                        mb: 3,
                        boxShadow: 3
                      }}
                    />
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {member.name}
                    </Typography>
                    <Chip 
                      label={member.position} 
                      color="primary" 
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(member.bio) }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No board members available at the moment.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Board Responsibilities */}
        <Paper sx={{ p: 6, mt: 8, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
          <Typography variant="h4" component="h2" gutterBottom textAlign="center" sx={{ mb: 6, fontWeight: 'bold' }}>
            Board Responsibilities
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Strategic Planning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Setting long-term goals and strategic direction for the hospital
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Financial Oversight
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ensuring financial stability and responsible resource management
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Quality Assurance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maintaining the highest standards of patient care and safety
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Governance Statement */}
        <Box sx={{ mt: 8, p: 4, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ fontWeight: 'bold' }}>
            Our Governance Commitment
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            The Board of Directors is committed to transparent governance, ethical leadership, and 
            continuous improvement. We work closely with hospital leadership to ensure that our 
            strategic decisions align with our mission of providing exceptional healthcare services 
            while maintaining the highest standards of medical excellence and patient care.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Board; 